import { useId, type SVGProps } from 'react';

/**
 * Inline brand-logo React components for every provider and tool shown
 * on the landing page. DESIGN.md §7 mandates inline SVG (not `<img>`):
 * the page stays single-request, logos can tint via `currentColor`
 * where appropriate, and CSP stays tight.
 *
 * Each component is a named export (no default exports per project
 * convention) and forwards `SVGProps<SVGSVGElement>` so the caller
 * controls sizing, className, role and aria attributes. The internal
 * `aria-hidden` default is overridable.
 *
 * Provider + tool marks (OpenAI, Anthropic, Claude, Cursor, VSCode,
 * JetBrains) use path data from simple-icons
 * (https://simpleicons.org — CC0-1.0 license), a community-maintained
 * set of official monochrome brand SVGs. Shapes follow each brand's
 * current brand guidelines as of 2026-04. Tint is via `currentColor`
 * for the neutral marks; VS Code keeps its official blue and
 * Claude Code keeps the Anthropic Claude orange as inline hex
 * (brand-locked colors, not page palette tokens).
 *
 * Gemini keeps the inline 4-color bloom gradient (its brand uses a
 * multi-stop gradient that a monochrome mark would lose), and the
 * gradient id is scoped via `useId()` so multiple logos on the same
 * page don't collide.
 *
 * `OpenCodeLogo` is the official two-tone square mark from opencode.ai
 * (32×40 viewBox, nested frames in `#211E1E` + `#BCBBBB`).
 * `OpenClawLogo` is the official lobster mark from
 * `openclaw.ai/favicon.svg` (120×120, red linear gradient body + cyan
 * eye dots + dark pupils) — its gradient id is also scoped via
 * `useId()` for the same reason as Gemini.
 *
 * Hex literals in this file are the single documented exception to
 * the project's "no hex in .tsx" rule: these ARE the brand colors for
 * third-party marks and would be wrong to expose as Tailwind tokens
 * (they would pollute the palette). See CLAUDE.md Do-NOT rule which
 * applies to local palette, not to brand assets.
 */

type LogoProps = SVGProps<SVGSVGElement>;

// ─── Provider logos ─────────────────────────────────────────────

export function OpenAILogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
      />
    </svg>
  );
}

export function AnthropicLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"
      />
    </svg>
  );
}

export function GeminiLogo(props: LogoProps) {
  const id = useId();
  const gradId = `gemini-grad-${id}`;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id={gradId} x1="16%" y1="0%" x2="84%" y2="100%">
          <stop offset="0%" stopColor="#1BA1E3" />
          <stop offset="30%" stopColor="#5489D6" />
          <stop offset="65%" stopColor="#9B72CB" />
          <stop offset="100%" stopColor="#D96570" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M12 0C12 6.627 17.373 12 24 12C17.373 12 12 17.373 12 24C12 17.373 6.627 12 0 12C6.627 12 12 6.627 12 0Z"
      />
    </svg>
  );
}

// ─── Tool logos ─────────────────────────────────────────────────

export function ClaudeCodeLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="#CC785C"
        d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z"
      />
    </svg>
  );
}

export function CursorLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        d="M11.925.02L.262 6.75v10.5l11.663 6.73 11.663-6.73V6.75L11.925.02zm0 2.227l9.47 5.467v1.67L11.924 14.8 2.457 9.384V7.715zm9.47 9.01v3.76l-9.47 5.466-9.47-5.467V11.26l9.47 5.467z"
      />
    </svg>
  );
}

export function VSCodeLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="#007ACC"
        d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"
      />
    </svg>
  );
}

export function OpenCodeLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 32 40" fill="none" aria-hidden="true" {...props}>
      <path d="M24 32H8V16H24V32Z" fill="#BCBBBB" />
      <path d="M24 8H8V32H24V8ZM32 40H0V0H32V40Z" fill="#211E1E" />
    </svg>
  );
}

export function JetBrainsLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        fill="currentColor"
        d="M0 0v24h24V0H0zm8.424 3.012h3.435v.941h-1.176v2.965H9.6V3.953H8.424v-.941zm-.236 0v.894h-2.07v.612H8v.847H6.118v.612h2.118v.941H5.082l-.047-3.906h3.153zm-2.635 7.623c0 .659-.565 1.082-1.459 1.082H2.071V7.812h2.023c.471 0 .847.141 1.082.329.142.141.236.377.236.659 0 .471-.235.706-.612.894.471.141.753.424.753.941zM2.682 5.647c.188.235.376.376.612.376.282 0 .424-.188.424-.565V2.965H4.8v2.494c0 .471-.141.847-.376 1.082s-.612.377-1.083.377c-.706.047-1.082-.236-1.364-.612l.705-.659zm8.377 15.341H2.071v-1.506h8.988v1.506zm.894-9.223l-.282-.706h-1.506l-.282.706h-1.13l.02-.047h-.679l-.8-1.176h-.47v1.176H5.741V7.812h1.835c.612 0 .988.141 1.271.424.235.235.329.518.329.894 0 .612-.329.988-.8 1.224l.605.878 1.465-3.419h1.035l1.647 3.953h-1.175zm2.4-.047h-1.082V7.812h1.082v3.906zm3.953 0h-.941L15.718 9.6v2.165h-1.082V7.812h.988l1.6 2.071V7.812h1.082v3.906zm1.929.047a2.488 2.488 0 0 1-1.835-.612l.612-.706c.376.329.8.471 1.271.471.282 0 .471-.094.471-.282s-.141-.235-.659-.376c-.847-.188-1.459-.424-1.459-1.224 0-.706.565-1.271 1.506-1.271.659 0 1.176.188 1.6.518l-.565.753c-.377-.236-.753-.377-1.083-.377-.282 0-.376.094-.376.235 0 .188.141.235.659.376.894.188 1.459.471 1.459 1.224-.001.8-.612 1.271-1.601 1.271z"
      />
    </svg>
  );
}

export function OpenClawLogo(props: LogoProps) {
  const id = useId();
  const gradId = `openclaw-grad-${id}`;
  return (
    <svg viewBox="0 0 120 120" fill="none" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF4D4D" />
          <stop offset="100%" stopColor="#991B1B" />
        </linearGradient>
      </defs>
      <path
        d="M60 10 C30 10 15 35 15 55 C15 75 30 95 45 100 L45 110 L55 110 L55 100 C55 100 60 102 65 100 L65 110 L75 110 L75 100 C90 95 105 75 105 55 C105 35 90 10 60 10Z"
        fill={`url(#${gradId})`}
      />
      <path
        d="M20 45 C5 40 0 50 5 60 C10 70 20 65 25 55 C28 48 25 45 20 45Z"
        fill={`url(#${gradId})`}
      />
      <path
        d="M100 45 C115 40 120 50 115 60 C110 70 100 65 95 55 C92 48 95 45 100 45Z"
        fill={`url(#${gradId})`}
      />
      <path d="M45 15 Q35 5 30 8" stroke="#FF4D4D" strokeWidth="3" strokeLinecap="round" />
      <path d="M75 15 Q85 5 90 8" stroke="#FF4D4D" strokeWidth="3" strokeLinecap="round" />
      <circle cx="45" cy="35" r="6" fill="#050810" />
      <circle cx="75" cy="35" r="6" fill="#050810" />
      <circle cx="46" cy="34" r="2.5" fill="#00E5CC" />
      <circle cx="76" cy="34" r="2.5" fill="#00E5CC" />
    </svg>
  );
}

// ─── Nadoutong brand mark ───────────────────────────────────────
//
// Network-flow logo: curved routing paths with gradient strokes
// (blue → cyan → purple) and white node circles at endpoints.
// From the official site-aware logo pack.

export function NadoutongLogo(props: LogoProps) {
  const id = useId();
  return (
    <svg viewBox="0 0 160 160" fill="none" aria-hidden="true" {...props}>
      <defs>
        <linearGradient
          id={`flow-${id}`}
          x1="18"
          y1="110"
          x2="118"
          y2="20"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2563EB" />
          <stop offset="0.52" stopColor="#06B6D4" />
          <stop offset="1" stopColor="#7C3AED" />
        </linearGradient>
        <linearGradient
          id={`soft-${id}`}
          x1="40"
          y1="74"
          x2="112"
          y2="44"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2563EB" stopOpacity="0.74" />
          <stop offset="1" stopColor="#7C3AED" stopOpacity="0.82" />
        </linearGradient>
      </defs>
      <g transform="translate(24 24) scale(0.62)">
        <path
          d="M72 55H126C148 55 166 73 166 95V111"
          stroke={`url(#flow-${id})`}
          strokeWidth="13.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M78 91H125C140 91 152 103 152 118V132"
          stroke={`url(#soft-${id})`}
          strokeWidth="10.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.9"
        />
        <path
          d="M78 129H120"
          stroke={`url(#soft-${id})`}
          strokeWidth="10.5"
          strokeLinecap="round"
          opacity="0.86"
        />
        <path
          d="M35 170C58 154 79 148 105 150H139C165 150 184 138 197 116"
          stroke={`url(#flow-${id})`}
          strokeWidth="14"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M35 170C58 154 79 148 105 150H139C165 150 184 138 197 116"
          stroke="white"
          strokeOpacity="0.48"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="72" cy="55" r="8" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="3.2" />
        <circle cx="166" cy="95" r="7.2" fill="#FFFFFF" stroke="#7C3AED" strokeWidth="3.2" />
        <circle cx="152" cy="132" r="6.5" fill="#FFFFFF" stroke="#2563EB" strokeWidth="3" />
        <circle cx="35" cy="170" r="8.5" fill="#FFFFFF" stroke="#2563EB" strokeWidth="3.3" />
        <circle cx="197" cy="116" r="8.5" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="3.3" />
      </g>
    </svg>
  );
}

// ─── Inline arrow (shared) ──────────────────────────────────────

export function ArrowRight(props: LogoProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
      {...props}
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
